import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/messaging';
import { Subscription, BehaviorSubject, Subject } from 'rxjs';
import { SnackBarService } from 'libs/shared/material/src/lib/services/snackbar.service';
import { MessageTabService } from './messages-tab.service';
import { Howl } from 'howler';
import { ToastKeys } from 'libs/shared/material/src/lib/types/snackbar.type';
import { ModalConfig } from '@hospitality-bot/admin/shared';

@Injectable({
  providedIn: 'root',
})
export class FirebaseMessagingService {
  currentMessage = new BehaviorSubject(null);
  liveRequestEnable = new BehaviorSubject(null);
  newInhouseRequest = new BehaviorSubject(null);
  private subscription: Subscription = new Subscription();
  tabActive = new BehaviorSubject(false);
  private processedMessageIds = new Set<string>();

  $receivedNewNotification = new BehaviorSubject(false);
  $newNotification = new BehaviorSubject<ModalConfig>(null);

  $refreshConversationStats = new Subject<any>();

  constructor(
    private fireMessaging: AngularFireMessaging,
    private messageTabService: MessageTabService,
    private _snackbarService: SnackBarService
  ) {
    this.subscription.add(
      this.fireMessaging.tokenChanges.subscribe((response) =>
        console.log('Messaging token Refreshed')
      )
    );
  }

  /**
   * Triggers event notifying subscribers that a new notification has been received.
   */
  receivedNewNotification() {
    this.$receivedNewNotification.next(true);
  }

  //#region Private Methods
  /**
   * Requests browser notification permissions from the user and retrieves FCM token if granted.
   * @param config Configuration containing entityId and userId.
   */
  requestPermission(config) {
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        this.getFirebaseToken(config);
      }
    });
  }

  /**
   * Obtains Firebase messaging token for push notifications and registers it with backend.
   * @param config Configuration object containing user details.
   */
  getFirebaseToken(config) {
    this.subscription.add(
      this.fireMessaging.requestToken.subscribe(
        (token) => {
          if (token) {
            this.registerFirebaseAuth(config, token);
          } else this.openNotificationAlert();
        },
        (error) => console.error('Unable to get permission to notify.', error)
      )
    );
  }

  /**
   * Helper method to determine if a notification payload is muted.
   * Checks isMute, isMuted, and muted properties in boolean, string, or numeric formats.
   * @param payload Firebase message payload.
   * @returns `true` if muted, `false` otherwise.
   */
  checkIsMuted(payload: any): boolean {
    if (!payload) return false;
    const data = payload.data || payload;
    const isMuteVal =
      data.isMute ??
      data.isMuted ??
      data.muted ??
      payload.isMute ??
      payload.isMuted;

    if (
      isMuteVal === true ||
      isMuteVal === 'true' ||
      isMuteVal === 1 ||
      isMuteVal === '1' ||
      (typeof isMuteVal === 'string' && isMuteVal.toLowerCase() === 'true')
    ) {
      return true;
    }
    return false;
  }

  /**
   * Listens to messages posted from the Service Worker (`FIREBASE_BG_MESSAGE`).
   * Handles audio sound, snackbar toast, and system notification triggering.
   */
  listenToServiceWorkerMessages() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event: any) => {
        if (event.data?.type === 'FIREBASE_BG_MESSAGE') {
          const payload = event.data.payload;
          this.receivedNewNotification();

          const isMuted = this.checkIsMuted(payload);

          if (!isMuted) {
            this.playNotificationSound(
              payload?.data?.notificationType,
              payload?.data?.isBuzz
            );
            this.showNotificationAsSnackBar(payload);
          }

          this.triggerSystemNotification(payload);
        }
      });
    }
  }

  /**
   * Checks whether the current browser tab is in the background or unfocused.
   * @returns `true` if document is hidden or window lost focus, `false` otherwise.
   */
  isTabInBackground(): boolean {
    return document.hidden || !document.hasFocus();
  }

  /**
   * Triggers Microsoft Teams-like OS desktop system notification when the tab is in the background.
   * Enforces rules for isMute, silentNotification, and proper contact name resolution.
   * @param payload Firebase message payload.
   */
  triggerSystemNotification(payload: any) {
    if (
      !payload ||
      !('Notification' in window) ||
      Notification.permission !== 'granted'
    ) {
      return;
    }

    const isMuted = this.checkIsMuted(payload);
    const isSilent =
      payload?.data?.silentNotification === 'true' ||
      payload?.data?.silentNotification === true;

    if (isMuted || isSilent) {
      return;
    }

    // Teams-like behavior: Only trigger System OS Notification if the tab is in background
    if (!this.isTabInBackground()) {
      return;
    }

    const messageId =
      payload?.fcmMessageId ||
      payload?.data?.notificationId ||
      (payload?.data?.phoneNumber || payload?.data?.nickName || '') +
        '_' +
        (payload?.data?.message || payload?.notification?.body || '');

    if (messageId && this.processedMessageIds.has(messageId)) {
      return;
    }
    if (messageId) {
      this.processedMessageIds.add(messageId);
      if (this.processedMessageIds.size > 100) {
        const firstVal = this.processedMessageIds.values().next().value;
        this.processedMessageIds.delete(firstVal);
      }
    }

    // Title resolution: Prefer nickName / contact name over phoneNumber / generic title
    let title =
      payload?.data?.nickName ||
      payload?.data?.name ||
      payload?.data?.guestName ||
      payload?.notification?.title ||
      payload?.data?.phoneNumber ||
      payload?.data?.notificationType ||
      'New Notification';

    let body =
      payload?.data?.message ||
      payload?.notification?.body ||
      payload?.data?.body ||
      '';

    if (body) {
      try {
        body = decodeURIComponent(body);
      } catch (e) {}
    }

    const options: NotificationOptions = {
      body: body,
      icon: 'assets/images/notification-icon.png',
      tag:
        payload?.data?.notificationId ||
        payload?.data?.conversationId ||
        undefined,
    };

    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.ready
          .then((registration) => {
            registration.showNotification(title, options);
          })
          .catch(() => {
            this.createFallbackSystemNotification(title, options);
          });
      } else {
        this.createFallbackSystemNotification(title, options);
      }
    } catch (e) {
      console.error('Error triggering system notification:', e);
    }
  }

  /**
   * Fallback system notification using standard HTML5 Notification constructor.
   * Attaches click handler to focus window when notification is clicked.
   * @param title Title of notification.
   * @param options NotificationOptions configuration.
   */
  private createFallbackSystemNotification(
    title: string,
    options: NotificationOptions
  ) {
    const notification = new Notification(title, options);
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  }

  /**
   * Sends FCM web push token to backend API for storage.
   * @param config User & Entity configuration.
   * @param token Firebase messaging token.
   */
  registerFirebaseAuth(config, token) {
    this.subscription.add(
      this.messageTabService
        .registerFirebaseMessage(config, {
          token: token,
          type: 'web',
        })
        .subscribe(
          (response) => console.log('Token stored on server'),
          ({ error }) =>
            console.error('Unable to store token on server ==> ', error.message)
        )
    );
  }

  /**
   * Opens dialog alert when notification permissions are not enabled.
   * @remarks $newNotification is subscribed in app.component for showing dialog
   */
  openNotificationAlert() {
    this.$newNotification.next({
      styleClass: 'confirm-dialog',
      width: 'unset',
    });
  }

  /**
   * Returns observable stream for incoming FCM foreground messages.
   * @remarks Subscribe this only in layout (main-code).
   * If subscribed in any other places than that will override the main one
   * use $receivedNewNotification for it. It will get triggered from receiveMessage subscription
   */
  receiveMessage() {
    return this.fireMessaging.messages;
  }

  /**
   * Displays in-app snackbar toast notification for incoming messages.
   * @param payload Notification message payload.
   */
  showNotificationAsSnackBar(payload: any) {
    if (this.checkIsMuted(payload)) {
      return;
    }

    if (payload?.data?.notificationType === 'WHATSAPP') {
      this._snackbarService.openSnackBarAsText(payload.data?.message, '', {
        duration: 5000,
        title: payload.data?.nickName ?? payload.data?.phoneNumber,
        key: ToastKeys.whatsappNotification,
      });
    } else if (payload.notification?.body) {
      const title = payload.notification?.body.split('\n')[0];
      this._snackbarService.openSnackBarAsText(
        `${payload.notification?.title}: ${decodeURIComponent(
          payload.notification?.body.substring(
            payload.notification?.body.split('\n').join(' | ')
          )
        )}`,
        '',
        {
          panelClass: 'notification',
          horizontalPosition: 'center',
        }
      );
    }
  }

  /**
   * Plays notification sound using Howler audio library if `isBuzz` condition is active.
   * @param notificationType Type of notification ('BOT' or standard).
   * @param isBuzz Buzz setting flag (string 'true' or boolean true).
   */
  playNotificationSound(notificationType, isBuzz: any) {
    const isBuzzActive = isBuzz === 'true' || isBuzz === true;
    if (!isBuzzActive) return;

    const sound =
      notificationType === 'BOT'
        ? new Howl({
            src: ['assets/audio/notification-buzzer.mp3'],
          })
        : new Howl({
            src: ['assets/audio/notification.mp3'],
          });
    sound.play();
  }
  //#endregion Public Methods

  /**
   * Unsubscribes active RxJS subscriptions and cleans up event listeners.
   */
  destroySubscription() {
    this.subscription.unsubscribe();
    navigator.serviceWorker.removeEventListener('message', () => {});
  }
}
